import {useQuery} from '@tanstack/react-query';

import {CosmosChainId} from 'app/chains/cosmos/cosmos.types';
import {
  ElysModuleMap,
  ElysModulePath,
  QueryChainModuleSchema,
  QueryOptionsSchema,
} from 'app/generated/types';
import {encodeChainPacket} from 'app/utils/encoder.utils';
import {useElysClient} from './useQueryClient';

export const formatQueryChainModuleKey = <
  T extends ElysModulePath = ElysModulePath,
>(
  query: QueryChainModuleSchema<T>,
) => {
  return [
    'module-query',
    JSON.stringify(query ? encodeChainPacket(query) : null),
  ];
};

export const useQueryChainModule = <T extends ElysModulePath = ElysModulePath>(
  query: QueryChainModuleSchema<T>,
  options?: QueryOptionsSchema,
  chainId?: CosmosChainId,
) => {
  const {data: elysClient, isLoading} = useElysClient(chainId);

  return useQuery({
    queryKey: formatQueryChainModuleKey(query),
    queryFn: async () => {
      /**
       * As of React query v5, suspense queries do not support "enabled" option.
       * This makes unauthenticated users to fetch user related data making the app to error out
       * when the user is logged out.
       **/
      if (options?.enabled === false) return null;

      const method = getObjectPropertyValue(elysClient, query.module) as any;
      const response = (await method(query.data)) as ElysModuleMap[T]['output'];

      return response;
    },
    refetchOnMount: false,
    staleTime: 60000,
    ...options,
    enabled:
      typeof options?.enabled !== 'undefined'
        ? options?.enabled && !isLoading
        : !isLoading,
  });
};

export const QUERY_DEFAULT_PAGINATION = {
  key: new Uint8Array(),
  limit: BigInt(10000),
  offset: BigInt(0),
  countTotal: false,
  reverse: false,
};

export const getObjectPropertyValue = <T>(object: T, path: string): unknown => {
  const parts = path.split('.').map(p => (isNaN(Number(p)) ? p : Number(p)));
  let current: unknown = object;

  for (const part of parts) {
    if (
      current === null ||
      typeof current !== 'object' ||
      !(part in current) ||
      !Object.prototype.hasOwnProperty.call(current, part)
    ) {
      return;
    }
    current = (current as Record<string | number, unknown>)[part];
  }

  return current;
};
